import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Typography,
  Chip,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { useQuery } from '@tanstack/react-query';
import { format, subDays } from 'date-fns';
import { reportsService } from '../../services/reports.service';
import { employeeService } from '../../services/employee.service';
import { DataTable } from '../../components/common/DataTable';
import { StatusChip } from '../../components/common/StatusChip';
import { ErrorMessage } from '../../components/common/ErrorMessage';
import { PageHeader } from '../../components/common/PageHeader';
import type { DailyReportRow, VisitReportRow, TrackingGapRow } from '../../types';

const today = format(new Date(), 'yyyy-MM-dd');
const weekAgo = format(subDays(new Date(), 7), 'yyyy-MM-dd');

interface TabPanelProps {
  children: React.ReactNode;
  value: number;
  index: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return value === index ? <Box>{children}</Box> : null;
}

export function ReportsPage() {
  const [tab, setTab] = useState(0);

  // Daily Report filters
  const [dailyDate, setDailyDate] = useState(today);
  const [dailyEmployee, setDailyEmployee] = useState('');

  // Visit Report filters
  const [visitStart, setVisitStart] = useState(weekAgo);
  const [visitEnd, setVisitEnd] = useState(today);
  const [visitEmployee, setVisitEmployee] = useState('');

  // Tracking Gap filters
  const [gapStart, setGapStart] = useState(weekAgo);
  const [gapEnd, setGapEnd] = useState(today);
  const [gapEmployee, setGapEmployee] = useState('');

  const { data: employees } = useQuery({
    queryKey: ['employees-all'],
    queryFn: () => employeeService.getAll({ limit: 200 }),
  });

  const {
    data: dailyData,
    isLoading: dailyLoading,
    error: dailyError,
    refetch: dailyRefetch,
  } = useQuery({
    queryKey: ['report-daily', dailyDate, dailyEmployee],
    queryFn: () => reportsService.getDailyReport({ date: dailyDate, employeeId: dailyEmployee || undefined }),
    enabled: tab === 0,
  });

  const {
    data: visitData,
    isLoading: visitLoading,
    error: visitError,
    refetch: visitRefetch,
  } = useQuery({
    queryKey: ['report-visits', visitStart, visitEnd, visitEmployee],
    queryFn: () =>
      reportsService.getVisitReport({
        startDate: visitStart,
        endDate: visitEnd,
        employeeId: visitEmployee || undefined,
      }),
    enabled: tab === 1,
  });

  const {
    data: gapData,
    isLoading: gapLoading,
    error: gapError,
    refetch: gapRefetch,
  } = useQuery({
    queryKey: ['report-gaps', gapStart, gapEnd, gapEmployee],
    queryFn: () =>
      reportsService.getTrackingGapReport({
        startDate: gapStart,
        endDate: gapEnd,
        employeeId: gapEmployee || undefined,
      }),
    enabled: tab === 2,
  });

  // Daily Report Columns
  const dailyColumns = [
    {
      id: 'employee',
      label: 'Employee',
      minWidth: 160,
      render: (row: DailyReportRow) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>{row.employee?.name}</Typography>
          <Typography variant="caption" color="text.secondary">{row.employee?.employeeCode}</Typography>
        </Box>
      ),
    },
    {
      id: 'status',
      label: 'Status',
      render: (row: DailyReportRow) => <StatusChip status={row.workday?.status ?? 'ABSENT'} />,
    },
    {
      id: 'checkIn',
      label: 'Check In',
      render: (row: DailyReportRow) => (
        <Typography variant="body2">{row.workday?.checkInTime ?? '—'}</Typography>
      ),
    },
    {
      id: 'checkOut',
      label: 'Check Out',
      render: (row: DailyReportRow) => (
        <Typography variant="body2">{row.workday?.checkOutTime ?? '—'}</Typography>
      ),
    },
    {
      id: 'workMinutes',
      label: 'Work Hours',
      render: (row: DailyReportRow) => {
        const mins = row.workday?.totalWorkMinutes ?? 0;
        const h = Math.floor(mins / 60);
        const m = mins % 60;
        return <Typography variant="body2">{mins ? `${h}h ${m}m` : '—'}</Typography>;
      },
    },
    {
      id: 'distance',
      label: 'Distance (km)',
      align: 'center' as const,
      render: (row: DailyReportRow) => (
        <Typography variant="body2">{row.workday?.totalDistanceKm?.toFixed(1) ?? '—'}</Typography>
      ),
    },
    {
      id: 'visits',
      label: 'Visits',
      align: 'center' as const,
      render: (row: DailyReportRow) => (
        <Typography variant="body2">
          {row.workday?.visitsCompleted ?? 0} /{' '}
          {(row.workday?.visitsCompleted ?? 0) + (row.workday?.visitsMissed ?? 0)}
        </Typography>
      ),
    },
  ];

  // Visit Report Columns
  const visitColumns = [
    {
      id: 'employee',
      label: 'Employee',
      minWidth: 140,
      render: (row: VisitReportRow) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>{row.employee?.name}</Typography>
          <Typography variant="caption" color="text.secondary">{row.employee?.employeeCode}</Typography>
        </Box>
      ),
    },
    {
      id: 'customer',
      label: 'Customer',
      render: (row: VisitReportRow) => (
        <Typography variant="body2">{row.customer?.name}</Typography>
      ),
    },
    {
      id: 'date',
      label: 'Date',
      render: (row: VisitReportRow) => (
        <Typography variant="body2">
          {format(new Date(row.visit.assignedDate), 'MMM d, yyyy')}
        </Typography>
      ),
    },
    {
      id: 'status',
      label: 'Status',
      render: (row: VisitReportRow) => <StatusChip status={row.visit.status} />,
    },
    {
      id: 'priority',
      label: 'Priority',
      render: (row: VisitReportRow) => <StatusChip status={row.visit.priority} />,
    },
    {
      id: 'duration',
      label: 'Duration',
      align: 'center' as const,
      render: (row: VisitReportRow) => (
        <Typography variant="body2">{row.duration ? `${row.duration} min` : '—'}</Typography>
      ),
    },
  ];

  // Tracking Gap Columns
  const gapColumns = [
    {
      id: 'employee',
      label: 'Employee',
      minWidth: 160,
      render: (row: TrackingGapRow) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>{row.employee?.name}</Typography>
          <Typography variant="caption" color="text.secondary">{row.employee?.employeeCode}</Typography>
        </Box>
      ),
    },
    {
      id: 'startTime',
      label: 'Start Time',
      render: (row: TrackingGapRow) => (
        <Typography variant="body2">
          {format(new Date(row.event.startTime), 'MMM d, HH:mm')}
        </Typography>
      ),
    },
    {
      id: 'endTime',
      label: 'End Time',
      render: (row: TrackingGapRow) => (
        <Typography variant="body2">
          {row.event.endTime ? format(new Date(row.event.endTime), 'HH:mm') : 'Ongoing'}
        </Typography>
      ),
    },
    {
      id: 'gap',
      label: 'Gap Duration',
      render: (row: TrackingGapRow) => (
        <Typography variant="body2" color="error.main" fontWeight={600}>
          {row.gapMinutes} min
        </Typography>
      ),
    },
    {
      id: 'status',
      label: 'Status',
      render: (row: TrackingGapRow) => (
        <Chip
          label={row.isResolved ? 'Resolved' : 'Active'}
          size="small"
          color={row.isResolved ? 'default' : 'error'}
          variant={row.isResolved ? 'outlined' : 'filled'}
        />
      ),
    },
  ];

  const handleExportDaily = () => {
    if (!dailyData) return;
    reportsService.exportToCSV(
      dailyData.map((r) => ({
        Employee: r.employee?.name,
        Code: r.employee?.employeeCode,
        Date: r.date,
        Status: r.workday?.status,
        CheckIn: r.workday?.checkInTime ?? '',
        CheckOut: r.workday?.checkOutTime ?? '',
        WorkMinutes: r.workday?.totalWorkMinutes ?? 0,
        DistanceKm: r.workday?.totalDistanceKm ?? 0,
        VisitsCompleted: r.workday?.visitsCompleted ?? 0,
        VisitsMissed: r.workday?.visitsMissed ?? 0,
      })) as Record<string, unknown>[],
      'daily_report'
    );
  };

  const handleExportVisits = () => {
    if (!visitData) return;
    reportsService.exportToCSV(
      visitData.map((r) => ({
        Employee: r.employee?.name,
        EmployeeCode: r.employee?.employeeCode,
        Customer: r.customer?.name,
        Date: r.visit.assignedDate,
        Status: r.visit.status,
        Priority: r.visit.priority,
        DurationMin: r.duration ?? '',
      })) as Record<string, unknown>[],
      'visit_report'
    );
  };

  const handleExportGaps = () => {
    if (!gapData) return;
    reportsService.exportToCSV(
      gapData.map((r) => ({
        Employee: r.employee?.name,
        EmployeeCode: r.employee?.employeeCode,
        StartTime: r.event.startTime,
        EndTime: r.event.endTime ?? '',
        GapMinutes: r.gapMinutes,
        Resolved: r.isResolved ? 'Yes' : 'No',
      })) as Record<string, unknown>[],
      'tracking_gap_report'
    );
  };

  return (
    <Box>
      <PageHeader
        compact
        title="Reports"
        subtitle="Analyze field force activity and performance"
      />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Daily Employee Report" />
        <Tab label="Visit Report" />
        <Tab label="Tracking Gap Report" />
      </Tabs>

      {/* Daily Report */}
      <TabPanel value={tab} index={0}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2.5} flexWrap="wrap" gap={2}>
              <Box display="flex" gap={1.5} flexWrap="wrap">
                <TextField
                  size="small"
                  label="Date"
                  type="date"
                  value={dailyDate}
                  onChange={(e) => setDailyDate(e.target.value)}
                  sx={{ minWidth: 160 }}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  size="small"
                  label="Employee"
                  select
                  value={dailyEmployee}
                  onChange={(e) => setDailyEmployee(e.target.value)}
                  sx={{ minWidth: 200 }}
                >
                  <MenuItem value="">All Employees</MenuItem>
                  {employees?.data.map((e) => (
                    <MenuItem key={e.id} value={e.id}>{e.name}</MenuItem>
                  ))}
                </TextField>
              </Box>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExportDaily}
                disabled={!dailyData?.length}
              >
                Export CSV
              </Button>
            </Box>
            {dailyError ? (
              <ErrorMessage onRetry={dailyRefetch} />
            ) : (
              <DataTable
                columns={dailyColumns}
                rows={dailyData ?? []}
                getRowId={(r) => r.employee?.id ?? Math.random().toString()}
                isLoading={dailyLoading}
                emptyMessage="No data for selected date"
              />
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Visit Report */}
      <TabPanel value={tab} index={1}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2.5} flexWrap="wrap" gap={2}>
              <Box display="flex" gap={1.5} flexWrap="wrap">
                <TextField
                  size="small"
                  label="Start Date"
                  type="date"
                  value={visitStart}
                  onChange={(e) => setVisitStart(e.target.value)}
                  sx={{ minWidth: 160 }}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  size="small"
                  label="End Date"
                  type="date"
                  value={visitEnd}
                  onChange={(e) => setVisitEnd(e.target.value)}
                  sx={{ minWidth: 160 }}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  size="small"
                  label="Employee"
                  select
                  value={visitEmployee}
                  onChange={(e) => setVisitEmployee(e.target.value)}
                  sx={{ minWidth: 200 }}
                >
                  <MenuItem value="">All Employees</MenuItem>
                  {employees?.data.map((e) => (
                    <MenuItem key={e.id} value={e.id}>{e.name}</MenuItem>
                  ))}
                </TextField>
              </Box>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExportVisits}
                disabled={!visitData?.length}
              >
                Export CSV
              </Button>
            </Box>
            {visitError ? (
              <ErrorMessage onRetry={visitRefetch} />
            ) : (
              <DataTable
                columns={visitColumns}
                rows={visitData ?? []}
                getRowId={(r) => r.visit?.id ?? Math.random().toString()}
                isLoading={visitLoading}
                emptyMessage="No visit data for selected range"
              />
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Tracking Gap Report */}
      <TabPanel value={tab} index={2}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2.5} flexWrap="wrap" gap={2}>
              <Box display="flex" gap={1.5} flexWrap="wrap">
                <TextField
                  size="small"
                  label="Start Date"
                  type="date"
                  value={gapStart}
                  onChange={(e) => setGapStart(e.target.value)}
                  sx={{ minWidth: 160 }}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  size="small"
                  label="End Date"
                  type="date"
                  value={gapEnd}
                  onChange={(e) => setGapEnd(e.target.value)}
                  sx={{ minWidth: 160 }}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  size="small"
                  label="Employee"
                  select
                  value={gapEmployee}
                  onChange={(e) => setGapEmployee(e.target.value)}
                  sx={{ minWidth: 200 }}
                >
                  <MenuItem value="">All Employees</MenuItem>
                  {employees?.data.map((e) => (
                    <MenuItem key={e.id} value={e.id}>{e.name}</MenuItem>
                  ))}
                </TextField>
              </Box>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExportGaps}
                disabled={!gapData?.length}
              >
                Export CSV
              </Button>
            </Box>
            {gapError ? (
              <ErrorMessage onRetry={gapRefetch} />
            ) : (
              <DataTable
                columns={gapColumns}
                rows={gapData ?? []}
                getRowId={(r) => r.event?.id ?? Math.random().toString()}
                isLoading={gapLoading}
                emptyMessage="No tracking gaps for selected range"
              />
            )}
          </CardContent>
        </Card>
      </TabPanel>
    </Box>
  );
}
